import { DecisionGraph, isDecisionGraph } from "@imminently/decisively-core";
import { initSync, layout as layoutWasm } from "./wasm/graph_layout";
import { GRAPH_LAYOUT_WASM } from "./wasm/bytes";

/** Marks a rule node that layoutGraphBytes added only to carry its instances' rule data (not one to draw). */
export const LAYOUT_TEMPLATE_FIELD = "layoutTemplate";

/** Fields of an instance's node that are its own (values and where it is), not its rule node's. */
const INSTANCE_FIELDS = ["input", "derived", "justification", "triggered", "debugInfo", "reason", "path", "index", "parent_path", "hidden"];

/**
 * A worker cannot access the release attached to a solved DG, so embed the selected nodes' rule metadata: an instance
 * takes its rule data from its template's global node, so a selected instance whose template isn't selected brings its
 * rule node along, marked LAYOUT_TEMPLATE_FIELD.
 */
export const layoutGraphBytes = (graph: DecisionGraph, ids: Iterable<number>): Uint8Array => {
  const selected = new Set([...ids].filter(id => graph.isListed(id)));
  const working = new DecisionGraph(graph.options);
  const workingIds = new Map<number, number>();
  const templates = new Set<string>();
  for (const id of selected) if (graph.isGlobal(id)) templates.add(graph.guid(id));
  for (const id of selected) {
    if (graph.isGlobal(id) || templates.has(graph.guid(id))) continue;
    const guid = graph.guid(id);
    templates.add(guid);
    const rule: Record<string, unknown> = { ...graph.node(id)!.toJSON() };
    for (const field of INSTANCE_FIELDS) delete rule[field];
    working.setNode(working.createRef(guid), { ...rule, hidden: true, [LAYOUT_TEMPLATE_FIELD]: true });
  }
  for (const id of selected) workingIds.set(id, working.setNode(working.createRef(graph.deref(id)), { ...graph.node(id)!.toJSON() }));
  for (const id of selected) {
    for (const to of graph.successors(id)) {
      const target = workingIds.get(to);
      if (target !== undefined) working.setEdge(workingIds.get(id)!, target);
    }
  }
  return working.toBytes();
};

/** How a graph is laid out: dagre's graph options, with the same defaults. */
export type LayoutOptions = {
  /** TB (the default), BT, LR or RL. */
  rankdir?: string;
  nodesep?: number;
  ranksep?: number;
  marginx?: number;
  marginy?: number;
};

export type Point = { x: number; y: number };

/** Laying out a DecisionGraph: which nodes to draw and how big each is. */
export type DecisionGraphLayoutOptions = LayoutOptions & {
  /** The nodes to lay out (node ids); every node with values of its own (`isListed`) when absent. */
  nodes?: Iterable<number>;
  /** Each node's size as drawn. */
  size: (id: number) => { width: number; height: number };
};

/** A laid-out DecisionGraph: each node's centre and each edge's points, as dagre.layout gave them. */
export type DecisionGraphLayout = {
  /** Each laid-out node's centre, by node id. */
  nodes: Map<number, Point>;
  /** The edges between laid-out nodes, from source to target. */
  edges: { from: number; to: number; points: Point[] }[];
  width: number;
  height: number;
};

/** Boxes to lay out (a pure UI layout, not a rule graph): each node's id and size, and the edges between them. */
export type LayoutBoxes = LayoutOptions & {
  nodes: { id: string; width?: number; height?: number }[];
  edges: { v: string; w: string }[];
};

/** Laid-out boxes: each node's centre by id (nodes only named by an edge too), each edge's points, and the size. */
export type BoxesLayout = {
  nodes: Map<string, Point>;
  edges: { v: string; w: string; points: Point[] }[];
  width: number;
  height: number;
};

/**
 * Lays out a graph top to bottom (or as `rankdir` says), as dagre.layout did. Takes either:
 *
 * - boxes (LayoutBoxes): node ids with their sizes and the edges between them, for UI layouts that aren't a rule
 *   graph; returns each box's centre, each edge's points and the drawing's size.
 * - a DecisionGraph, with the nodes to draw and their sizes: returns their positions (DecisionGraphLayout), reading
 *   the edges from the graph itself, so nothing is written out as another graph first.
 *
 * The layout is a layered (Sugiyama) layout in Rust (apps/portal/graph-layout): the QUT rule graph (1,885 nodes, 3,949
 * edges) takes about half a second natively where dagre took 8.4 s.
 */
export function layout(boxes: LayoutBoxes): BoxesLayout;
export function layout(graph: DecisionGraph, options: DecisionGraphLayoutOptions): DecisionGraphLayout;
export function layout(
  graph: LayoutBoxes | DecisionGraph,
  options?: DecisionGraphLayoutOptions,
): DecisionGraphLayout | BoxesLayout {
  if (isDecisionGraph(graph)) {
    if (!options) throw new Error("Laying out a DecisionGraph needs the nodes' sizes");
    return layoutDecisionGraph(graph, options);
  }
  return layoutBoxes(graph);
}

const layoutBoxes = (boxes: LayoutBoxes): BoxesLayout => {
  const index = new Map<string, number>();
  const sizes: { width: number; height: number }[] = [];
  const ids: string[] = [];
  for (const node of boxes.nodes) {
    // one box per id, the last size given winning (as setNode replaced the label)
    const at = index.get(node.id);
    const size = { width: Number(node.width) || 0, height: Number(node.height) || 0 };
    if (at !== undefined) sizes[at] = size;
    else {
      index.set(node.id, ids.length);
      ids.push(node.id);
      sizes.push(size);
    }
  }
  // an edge to a node not given adds it without a size (as graphlib's setEdge did)
  const at = (id: string) => {
    let i = index.get(id);
    if (i === undefined) {
      index.set(id, (i = ids.length));
      ids.push(id);
      sizes.push({ width: 0, height: 0 });
    }
    return i;
  };
  const pairs: number[] = [];
  for (const edge of boxes.edges) pairs.push(at(edge.v), at(edge.w));
  const placed = place(sizes, Uint32Array.from(pairs), boxes);
  const nodes = new Map(ids.map((id, i) => [id, placed.centres[i]]));
  return {
    nodes,
    edges: boxes.edges.map(({ v, w }) => ({ v, w, points: edgePoints(nodes.get(v)!, nodes.get(w)!) })),
    width: placed.width,
    height: placed.height,
  };
};

const layoutDecisionGraph = (graph: DecisionGraph, options: DecisionGraphLayoutOptions): DecisionGraphLayout => {
  const ids: number[] = [];
  const index = new Map<number, number>();
  const add = (id: number) => {
    if (index.has(id) || !graph.isListed(id)) return;
    index.set(id, ids.length);
    ids.push(id);
  };
  if (options.nodes) for (const id of options.nodes) add(id);
  else for (const id of graph.ids()) add(id);

  const pairs: number[] = [];
  for (const id of ids) {
    for (const to of graph.successors(id)) {
      const target = index.get(to);
      if (target !== undefined) pairs.push(index.get(id)!, target);
    }
  }

  const placed = place(
    ids.map((id) => options.size(id)),
    Uint32Array.from(pairs),
    options,
  );
  const nodes = new Map(ids.map((id, i) => [id, placed.centres[i]]));
  const edges: DecisionGraphLayout["edges"] = [];
  for (let i = 0; i < pairs.length; i += 2) {
    const from = ids[pairs[i]];
    const to = ids[pairs[i + 1]];
    edges.push({ from, to, points: edgePoints(nodes.get(from)!, nodes.get(to)!) });
  }
  return { nodes, edges, width: placed.width, height: placed.height };
};

/**
 * Lays out nodes of these sizes joined by these edges (`[from0, to0, from1, to1, ...]` as indexes into `sizes`): each
 * node's centre, in `sizes`' order, and the drawing's size including the margins.
 */
const place = (sizes: { width: number; height: number }[], edges: Uint32Array, options: LayoutOptions) => {
  ensureStarted();
  const rankdir = String(options.rankdir ?? "TB").toUpperCase();
  const horizontal = rankdir === "LR" || rankdir === "RL";
  const nodesep = options.nodesep ?? 50;
  const ranksep = options.ranksep ?? 50;
  const marginx = options.marginx ?? 0;
  const marginy = options.marginy ?? 0;

  // laid out top to bottom: a horizontal graph is laid out with each node's width and height swapped, then turned
  const flat = new Float64Array(sizes.length * 2);
  sizes.forEach(({ width, height }, i) => {
    flat[i * 2] = horizontal ? height : width;
    flat[i * 2 + 1] = horizontal ? width : height;
  });
  const out = layoutWasm(flat, edges, nodesep, ranksep);
  const width = out[sizes.length * 2];
  const height = out[sizes.length * 2 + 1];
  const turn = (x: number, y: number): Point => {
    switch (rankdir) {
      case "BT":
        return { x, y: height - y };
      case "LR":
        return { x: y, y: x };
      case "RL":
        return { x: height - y, y: x };
      default:
        return { x, y };
    }
  };
  const centres = sizes.map((_, i) => {
    const { x, y } = turn(out[i * 2], out[i * 2 + 1]);
    return { x: x + marginx, y: y + marginy };
  });
  return {
    centres,
    width: (horizontal ? height : width) + 2 * marginx,
    height: (horizontal ? width : height) + 2 * marginy,
  };
};

/** An edge from its source's centre to its target's (the views draw edges between node handles). */
const edgePoints = (from: Point, to: Point): Point[] => [
  { x: from.x, y: from.y },
  { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 },
  { x: to.x, y: to.y },
];

let started = false;
const ensureStarted = () => {
  if (started) return;
  const binary = atob(GRAPH_LAYOUT_WASM);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  initSync({ module: bytes });
  started = true;
};
